import React, { useRef, useState, useEffect, useMemo } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import {
  ChevronLeftIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  CreditCardIcon,
  CalendarDaysIcon,
  ArrowPathIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import AddPaymentMethod from './AddPaymentMethod';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { RRule, rrulestr } from 'rrule';

dayjs.extend(utc);

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

// Helper to parse RRule
const parseRrule = (icalString) => {
  if (!icalString) return null;
  try {
    const rule = rrulestr(icalString);
    return rule;
  } catch (e) {
    console.error("Error parsing RRULE:", e);
    return null;
  }
};

// Generate autopay RRule based on fund schedule and offset
const generateAutopayRrule = (fundRruleIcal, offset, invoiceLeadTime) => {
  const fundRule = parseRrule(fundRruleIcal);
  if (!fundRule) return '';

  try {
    const nextDue = fundRule.after(new Date(), true);
    if (!nextDue) return '';

    const targetDate = new Date(nextDue);
    targetDate.setUTCDate(targetDate.getUTCDate() - offset);

    const options = {
      freq: fundRule.options.freq,
      interval: fundRule.options.interval,
      dtstart: targetDate,
    };

    if (fundRule.options.freq === RRule.YEARLY) {
      options.bymonth = [targetDate.getUTCMonth() + 1];
      options.bymonthday = [targetDate.getUTCDate()];
    } else if (fundRule.options.freq === RRule.MONTHLY) {
      options.bymonthday = [targetDate.getUTCDate()];
    } else if (fundRule.options.freq === RRule.WEEKLY) {
      const weekdays = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];
      options.byweekday = [weekdays[targetDate.getUTCDay()]];
    }

    return new RRule(options).toString();
  } catch (e) {
    console.error("Error generating autopay rule:", e);
    return '';
  }
};

// Get next due date from the fund schedule
const getNextDueDate = (fundRruleIcal) => {
  const fundRule = parseRrule(fundRruleIcal);
  if (!fundRule) return null;

  try {
    return fundRule.after(new Date(), true) || null;
  } catch (e) {
    return null;
  }
};

// Get next charge date from the stored autopay RRULE (for deriving existing offset when editing)
const getNextAutopayOccurrence = (autopayRruleIcal) => {
  const rule = parseRrule(autopayRruleIcal);
  if (!rule) return null;
  try {
    return rule.after(new Date(), true) || null;
  } catch (e) {
    return null;
  }
};

// Format date as YYYY-MM-DD for datepicker value
const toDateString = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Format date nicely
const formatDate = (date) => {
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

// Inline calendar for charge date (always visible, no popover)
function InlineChargeDateCalendar({ value, minDate, maxDate, dueDate, onChange }) {
  const min = minDate ? dayjs(minDate).utc().startOf('day') : null;
  const max = maxDate ? dayjs(maxDate).utc().startOf('day') : null;

  const [viewMonth, setViewMonth] = useState(() => {
    if (value) return dayjs(value).utc().startOf('month');
    if (dueDate) return dayjs(dueDate).utc().startOf('month');
    return dayjs().utc().startOf('month');
  });

  const startOfMonth = viewMonth.startOf('month');
  const daysInMonth = viewMonth.daysInMonth();
  const firstDayOfWeek = startOfMonth.day();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const canPrev = min && startOfMonth.isAfter(min.startOf('month'));
  const nextMonthStart = startOfMonth.add(1, 'month');
  const canNext = max && !nextMonthStart.isAfter(max);

  const isSelectable = (d) => {
    const day = startOfMonth.date(d);
    if (min && day.isBefore(min)) return false;
    if (max && day.isAfter(max)) return false;
    return true;
  };
  const isSelected = (d) => value && dayjs(value).utc().date() === d && viewMonth.isSame(dayjs(value).utc(), 'month');
  const isDue = (d) => dueDate && dayjs(dueDate).utc().date() === d && viewMonth.isSame(dayjs(dueDate).utc(), 'month');

  const handleSelect = (d) => {
    if (!isSelectable(d)) return;
    const date = startOfMonth.date(d).toDate();
    date.setUTCHours(0, 0, 0, 0);
    onChange(date);
  };

  const days = [];
  const leadingBlanks = firstDayOfWeek;
  for (let i = 0; i < leadingBlanks; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  return (
    <div className="inline-block rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setViewMonth((m) => m.subtract(1, 'month'))}
          disabled={!canPrev}
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <span className="text-sm font-semibold text-gray-900">
          {viewMonth.format('MMMM YYYY')}
        </span>
        <button
          type="button"
          onClick={() => setViewMonth((m) => m.add(1, 'month'))}
          disabled={!canNext}
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          aria-label="Next month"
        >
          <ChevronLeftIcon className="w-5 h-5 rotate-180" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {weekDays.map((wd) => (
          <div key={wd} className="py-1 text-xs font-medium text-gray-500">
            {wd}
          </div>
        ))}
        {days.map((d, i) => {
          if (d === null) return <div key={`blank-${i}`} />;
          const selectable = isSelectable(d);
          const selected = isSelected(d);
          const due = isDue(d);
          const selectedAndDue = selected && due;
          return (
            <button
              key={d}
              type="button"
              onClick={() => handleSelect(d)}
              disabled={!selectable}
              className={classNames(
                'h-9 w-9 rounded-lg text-sm font-medium transition-colors',
                !selectable && 'text-gray-300 cursor-not-allowed',
                selectable && !selected && !due && 'text-gray-700 hover:bg-gray-100',
                selectable && due && !selected && 'text-amber-700 bg-amber-50 hover:bg-amber-100',
                selected && !due && 'bg-indigo-600 text-white hover:bg-indigo-700',
                selectedAndDue && 'bg-indigo-600 text-white hover:bg-indigo-700 ring-2 ring-amber-400 ring-offset-2'
              )}
            >
              {d}
            </button>
          );
        })}
      </div>
      {dueDate && (
        <p className="mt-2 text-xs text-gray-500 text-center">
          <span className="inline-block w-3 h-3 rounded bg-amber-200 align-middle mr-1" /> Due date
        </p>
      )}
    </div>
  );
}

// Payment Method Card Component
function PaymentMethodCard({ method, selected, onSelect }) {
  const brandColors = {
    visa: 'from-blue-600 to-blue-800',
    mastercard: 'from-red-500 to-orange-500',
    amex: 'from-blue-400 to-blue-600',
    discover: 'from-orange-400 to-orange-600',
    default: 'from-gray-600 to-gray-800'
  };

  const brandColor = brandColors[method.card.brand.toLowerCase()] || brandColors.default;

  return (
    <button
      type="button"
      onClick={() => onSelect(method)}
      className={classNames(
        'relative w-full p-4 rounded-xl border-2 transition-all duration-200 text-left',
        selected
          ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500 ring-offset-2'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
      )}
    >
      <div className="flex items-center gap-4">
        <div className={classNames(
          'w-14 h-9 rounded-md bg-gradient-to-br flex items-center justify-center shadow-sm',
          brandColor
        )}>
          <span className="text-xs font-bold text-white tracking-wider">
            {method.card.brand.toUpperCase().slice(0, 4)}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">
            •••• •••• •••• {method.card.last4}
          </p>
          <p className="text-xs text-gray-500">
            Expires {method.card.exp_month}/{method.card.exp_year}
          </p>
        </div>
        {selected && (
          <CheckCircleIcon className="w-6 h-6 text-indigo-600 flex-shrink-0" />
        )}
      </div>
    </button>
  );
}

// Main Modal Content
function ModalContent({
  isOpen,
  setOpen,
  actionUrl,
  paymentMethods,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  fundRruleIcal,
  initialAutopayRruleIcal,
  stripePublishableKey,
  customer,
  createCardPath,
  invoiceGenerationLeadTimeDays,
  hasExistingAutopay,
  cancelAutopayUrl,
  nextAutopayDate: existingNextAutopayDate,
  autopayAmount
}) {
  const cancelButtonRef = useRef(null);

  // View states
  const [view, setView] = useState('main'); // 'main', 'add-card', 'cancel-confirm'
  const [selectedChargeDate, setSelectedChargeDate] = useState(null); // Date or null
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Due date and valid charge date range from fund schedule
  const dueDate = useMemo(() => getNextDueDate(fundRruleIcal), [fundRruleIcal]);
  const { minChargeDate, maxChargeDate } = useMemo(() => {
    if (!dueDate) return { minChargeDate: null, maxChargeDate: null };
    const due = new Date(dueDate);
    due.setUTCHours(0, 0, 0, 0);
    const leadDays = Math.max(0, invoiceGenerationLeadTimeDays - 1);
    const min = new Date(due);
    min.setUTCDate(min.getUTCDate() - leadDays);
    // End of day on due date so the due date is selectable (picker may compare in local time)
    const max = new Date(due);
    max.setUTCHours(23, 59, 59, 999);
    return { minChargeDate: min, maxChargeDate: max };
  }, [dueDate, invoiceGenerationLeadTimeDays]);

  // Derive initial selected charge date when modal opens
  useEffect(() => {
    if (!isOpen || !dueDate || !maxChargeDate || !minChargeDate) return;
    setView('main');

    const due = new Date(dueDate);
    due.setUTCHours(0, 0, 0, 0);

    if (hasExistingAutopay && initialAutopayRruleIcal) {
      const nextCharge = getNextAutopayOccurrence(initialAutopayRruleIcal);
      if (nextCharge) {
        const charge = new Date(nextCharge);
        charge.setUTCHours(0, 0, 0, 0);
        const time = charge.getTime();
        if (time >= minChargeDate.getTime() && time <= maxChargeDate.getTime()) {
          setSelectedChargeDate(charge);
          return;
        }
      }
    }

    // Default: 3 days before due date if within range, else due date
    const defaultCharge = new Date(due);
    defaultCharge.setUTCDate(defaultCharge.getUTCDate() - 3);
    if (defaultCharge.getTime() >= minChargeDate.getTime()) {
      setSelectedChargeDate(defaultCharge);
    } else {
      setSelectedChargeDate(new Date(due));
    }
  }, [isOpen, dueDate, minChargeDate, maxChargeDate, hasExistingAutopay, initialAutopayRruleIcal]);

  // Computed offset in days (0 = charge on due date)
  const offsetDays = useMemo(() => {
    if (!dueDate || !selectedChargeDate) return 0;
    const due = new Date(dueDate);
    due.setUTCHours(0, 0, 0, 0);
    const charge = new Date(selectedChargeDate);
    charge.setUTCHours(0, 0, 0, 0);
    return Math.max(0, Math.round((due.getTime() - charge.getTime()) / 86400000));
  }, [dueDate, selectedChargeDate]);

  // Generate autopay RRULE from selected charge date (offset in days)
  const autopayRrule = useMemo(() => {
    return generateAutopayRrule(fundRruleIcal, offsetDays, invoiceGenerationLeadTimeDays);
  }, [fundRruleIcal, offsetDays, invoiceGenerationLeadTimeDays]);

  const handleStopAutopay = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(cancelAutopayUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector("[name='csrf-token']")?.content,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        window.location.reload();
      } else {
        console.error('Failed to stop autopay');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error stopping autopay:', error);
      setIsSubmitting(false);
    }
  };

  const canSubmit = selectedPaymentMethod && autopayRrule && (!dueDate || selectedChargeDate);

  // Render based on view
  const renderContent = () => {
    // Cancel confirmation view
    if (view === 'cancel-confirm') {
      return (
        <div className="text-center py-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <ExclamationTriangleIcon className="w-7 h-7 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancel AutoPay?</h3>
          <p className="text-sm text-gray-500 mb-4">
            Your automatic payments will stop. You'll need to pay manually going forward.
          </p>
          {existingNextAutopayDate && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-left">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Your scheduled payment of {autopayAmount} on {existingNextAutopayDate} will be cancelled.
              </p>
            </div>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setView('main')}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Keep AutoPay
            </button>
            <button
              type="button"
              onClick={handleStopAutopay}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Cancelling...' : 'Yes, Cancel'}
            </button>
          </div>
        </div>
      );
    }

    // Add payment method view
    if (view === 'add-card') {
      return (
        <div>
          <button
            type="button"
            onClick={() => setView('main')}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Back
          </button>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Payment Method</h3>
          <AddPaymentMethod
            stripe_publishable_key={stripePublishableKey}
            customer={customer}
            showCard={true}
            create_card_path={createCardPath}
          />
        </div>
      );
    }

    // No payment methods view
    if (!paymentMethods || paymentMethods.length === 0) {
      return (
        <div className="text-center py-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
            <CreditCardIcon className="w-8 h-8 text-indigo-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Add a Payment Method</h3>
          <p className="text-sm text-gray-500 mb-6">
            To set up automatic payments, you'll need to add a card first.
          </p>
          <button
            type="button"
            onClick={() => setView('add-card')}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Add Card
          </button>
        </div>
      );
    }

    // Main setup/manage view
    return (
      <form action={actionUrl} method="POST">
        <input type="hidden" name="authenticity_token" value={document.querySelector("[name='csrf-token']")?.content} />
        <input type="hidden" name="payment_method" value={selectedPaymentMethod?.id || ''} />
        <input type="hidden" name="autopay_rrule" value={autopayRrule} />

        {/* Header */}
        <div className="text-center mb-6">
          <div className={classNames(
            'mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-3',
            hasExistingAutopay ? 'bg-green-100' : 'bg-indigo-100'
          )}>
            <ArrowPathIcon className={classNames(
              'w-7 h-7',
              hasExistingAutopay ? 'text-green-600' : 'text-indigo-600'
            )} />
          </div>
          <h3 className="text-xl font-semibold text-gray-900">
            {hasExistingAutopay ? 'Manage AutoPay' : 'Set Up AutoPay'}
          </h3>
          {hasExistingAutopay && existingNextAutopayDate && (
            <p className="text-sm text-gray-500 mt-1">
              Next payment: {autopayAmount} on {existingNextAutopayDate}
            </p>
          )}
        </div>

        {/* Step 1: Payment Method */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-900">Payment Method</h4>
            <button
              type="button"
              onClick={() => setView('add-card')}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
            >
              + Add new
            </button>
          </div>
          <div className="space-y-2">
            {paymentMethods.map((method) => (
              <PaymentMethodCard
                key={method.id}
                method={method}
                selected={selectedPaymentMethod?.id === method.id}
                onSelect={setSelectedPaymentMethod}
              />
            ))}
          </div>
        </div>

        {/* Step 2: When to Charge — calendar */}
        {dueDate && minChargeDate && maxChargeDate && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">When to Charge</h4>
            <p className="text-sm text-gray-600 mb-2">
              Due date: <span className="font-semibold text-gray-900">{formatDate(dueDate)}</span>
            </p>
            <p className="text-xs text-gray-500 mb-3">
              Pick a charge date within the next {invoiceGenerationLeadTimeDays} days before the due date.
            </p>
            <div className="flex justify-center">
              <InlineChargeDateCalendar
                value={selectedChargeDate}
                minDate={minChargeDate}
                maxDate={maxChargeDate}
                dueDate={dueDate}
                onChange={setSelectedChargeDate}
              />
            </div>
            {selectedChargeDate && (
              <p className="mt-3 text-sm text-gray-600">
                {offsetDays === 0 ? (
                  <>Your card will be charged <strong>on the due date</strong>.</>
                ) : (
                  <>Your card will be charged <strong>{offsetDays} day{offsetDays !== 1 ? 's' : ''} before</strong> the due date.</>
                )}
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full px-4 py-3 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {hasExistingAutopay ? 'Update AutoPay' : 'Enable AutoPay'}
          </button>

          {hasExistingAutopay && (
            <button
              type="button"
              onClick={() => setView('cancel-confirm')}
              className="w-full px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
            >
              Cancel AutoPay
            </button>
          )}
        </div>
      </form>
    );
  };

  return (
    <Transition as="div" show={isOpen}>
      <Dialog className="relative z-50" onClose={setOpen} initialFocus={cancelButtonRef}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform rounded-2xl bg-white px-6 py-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
                {/* Close button */}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="absolute top-4 right-4 p-1 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
                  ref={cancelButtonRef}
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>

                {renderContent()}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

const AutoPayModal = (props) => {
  const {
    actionUrl,
    paymentMethods,
    fundRruleIcal,
    initialAutopayRruleIcal,
    stripePublishableKey,
    customer,
    createCardPath,
    invoiceGenerationLeadTimeDays,
    hasExistingAutopay,
    cancelAutopayUrl,
    nextAutopayDate,
    autopayAmount,
    buttonText: customButtonText,
    buttonClass: customButtonClass
  } = props;

  const [open, setOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(paymentMethods?.[0] || null);

  useEffect(() => {
    if (!selectedPaymentMethod && paymentMethods?.length > 0) {
      setSelectedPaymentMethod(paymentMethods[0]);
    }
  }, [paymentMethods, selectedPaymentMethod]);

  const buttonText = customButtonText || (hasExistingAutopay ? 'Manage AutoPay' : 'Set Up AutoPay');
  const buttonClass = customButtonClass || "inline-flex items-center justify-center gap-2 rounded-lg border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors";

  return (
    <>
      <button
        type="button"
        className={buttonClass}
        onClick={() => setOpen(true)}
      >
        <ArrowPathIcon className="w-4 h-4" />
        {buttonText}
      </button>
      <ModalContent
        isOpen={open}
        setOpen={setOpen}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        paymentMethods={paymentMethods}
        actionUrl={actionUrl}
        fundRruleIcal={fundRruleIcal}
        initialAutopayRruleIcal={initialAutopayRruleIcal}
        stripePublishableKey={stripePublishableKey}
        customer={customer}
        createCardPath={createCardPath}
        invoiceGenerationLeadTimeDays={invoiceGenerationLeadTimeDays}
        hasExistingAutopay={hasExistingAutopay}
        cancelAutopayUrl={cancelAutopayUrl}
        nextAutopayDate={nextAutopayDate}
        autopayAmount={autopayAmount}
      />
    </>
  );
};

export default AutoPayModal;
